import { useEffect, useMemo, useState } from 'react';
import request from '../assets/utils/http-request';

/**
 * Reads reference lists and form settings from GET /v2/config/forms (public).
 * Returns helpers that fall back to the built-in defaults a caller passes, so
 * a form renders identically when the config is absent or unreachable.
 *
 *   const { fieldProps, options, ready } = useFormConfig({ forms: ['org_client'], lists: ['state'] });
 *   <Form.Item {...fieldProps('org_client', 'state', { label: 'State', required: false })}>
 *   <Select options={options('state', states)} />
 */
const cache = {};

export default function useFormConfig({ forms = [], lists = [] } = {}) {
  const key = `${forms.join(',')}|${lists.join(',')}`;
  const [data, setData] = useState(cache[key] || null);

  useEffect(() => {
    let alive = true;
    if (cache[key]) { setData(cache[key]); return undefined; }
    request
      .get('/v2/config/forms', { params: { forms: forms.join(','), lists: lists.join(',') } })
      .then((resp) => {
        if (!alive) return;
        const d = resp?.data?.data;
        if (d && typeof d === 'object') { cache[key] = d; setData(d); }
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return useMemo(() => {
    const fieldsOf = (formKey) => (data?.forms?.[formKey]) || null;
    const field = (formKey, fieldKey) => (fieldsOf(formKey) || []).find((f) => f.field_key === fieldKey) || null;
    return {
      ready: !!data,
      /** Visible/required/label/help for a known field; defaults when unconfigured. */
      fieldProps: (formKey, fieldKey, defaults = {}) => {
        const f = field(formKey, fieldKey);
        if (!f) return { hidden: false, label: defaults.label, required: !!defaults.required, extra: defaults.extra };
        return { hidden: false, label: f.label || defaults.label, required: !!f.required, extra: f.help_text || defaults.extra };
      },
      /** True when the admin hid the field (only when the form is configured at all). */
      isHidden: (formKey, fieldKey) => {
        const list = fieldsOf(formKey);
        if (!list) return false;
        return !list.some((f) => f.field_key === fieldKey);
      },
      /** Options for a list, as {label, value}; defaults when the list is unconfigured. */
      options: (listKey, defaults = []) => {
        const opts = data?.lists?.[listKey];
        if (!Array.isArray(opts) || !opts.length) return defaults;
        return opts.map((o) => ({ label: o.label, value: o.code }));
      },
      /** Order of a form's fields, for callers that render dynamically. */
      order: (formKey) => (fieldsOf(formKey) || []).map((f) => f.field_key),
    };
  }, [data]);
}
